import { CONFIG } from "../config.ts";
import { clamp } from "../math.ts";

// The Three Poisons — the game's skill layer.
//
//   Ignorance  → mindful pacing:  gorging (eating too fast) tanks a bite's worth
//                and collapses your Mindfulness multiplier; eating on the breath
//                grows it.
//   Attachment → variety:         eating the same source over and over decays its
//                worth; new sources refresh it and feed the multiplier.
//   Aversion   → fullness:        every bite defiles you; near-full you slow and
//                score less; full and you must cleanse (the mandala mini-game).
//
// This object owns those meters and turns each bite into a final value.

export interface BiteResult {
  value: number; // mindful-adjusted worth (drives both enlightenment + karma)
  greedy: boolean;
  onBeat: boolean;
  newType: boolean;
  full: boolean; // defilement hit the ceiling → must cleanse
}

export class Poisons {
  mindfulness = 1; // multiplier, 1 .. mindfulMax
  defilement = 0; // 0 .. fullness.max
  clarity = 0; // remaining seconds of the post-cleanse clarity buff

  private clock = 0; // sim time
  private breathT = 0; // breath cadence clock
  private lastBiteAt = -999;
  private lastType = ""; // most-recent source, for the attachment streak
  private repeatRun = 0; // how many times in a row that source was eaten

  reset() {
    this.mindfulness = 1;
    this.defilement = 0;
    this.clarity = 0;
    this.clock = 0;
    this.breathT = 0;
    this.lastBiteAt = -999;
    this.lastType = "";
    this.repeatRun = 0;
  }

  update(dt: number) {
    this.clock += dt;
    this.breathT += dt;
    if (this.clarity > 0) this.clarity = Math.max(0, this.clarity - dt);
    // Mindfulness gently relaxes toward 1 so the rhythm must be kept up.
    if (this.mindfulness > 1) this.mindfulness = Math.max(1, this.mindfulness - 0.06 * dt);
  }

  /** Breath ring scale in [0,1]; peaks (== eat now) at 1. */
  get breathScale(): number {
    const phase = (this.breathT % CONFIG.poisons.breathPeriod) / CONFIG.poisons.breathPeriod;
    return 0.5 + 0.5 * Math.cos(phase * Math.PI * 2);
  }

  private get onBeat(): boolean {
    const p = CONFIG.poisons;
    const phase = (this.breathT % p.breathPeriod) / p.breathPeriod;
    const w = p.breathWindow / p.breathPeriod;
    return phase < w || phase > 1 - w;
  }

  get fullnessFrac(): number {
    return this.defilement / CONFIG.fullness.max;
  }

  get overfull(): boolean {
    return this.fullnessFrac >= CONFIG.fullness.slowThreshold;
  }

  /** Speed multiplier from being weighed down by filth. */
  get speedFactor(): number {
    return this.overfull ? CONFIG.fullness.slowSpeedFactor : 1;
  }

  /**
   * Process a bite. `base` is the poop's intrinsic worth (tier); `sourceType`
   * identifies who dropped it (for the attachment streak); `stage` is the realm.
   */
  eat(base: number, sourceType: string, stage: number): BiteResult {
    const p = CONFIG.poisons;
    const greedy = this.clock - this.lastBiteAt < p.mindfulWindow;
    const onBeat = this.onBeat && !greedy;
    const newType = sourceType !== this.lastType;

    // Attachment: consecutive same-source bites are worth progressively less.
    if (newType) this.repeatRun = 0;
    else this.repeatRun++;
    const attachment = Math.max(p.attachmentFloor, 1 - p.attachmentPenalty * this.repeatRun);

    // Ignorance: update the Mindfulness multiplier.
    if (greedy) {
      this.mindfulness = Math.max(1, this.mindfulness * p.mindfulGreedPenalty);
    } else {
      const gain =
        p.mindfulGainPerBite + (onBeat ? p.breathBonusGain : 0) + (newType ? p.varietyGainPerNew : 0);
      this.mindfulness = Math.min(p.mindfulMax, this.mindfulness + gain);
    }

    const pace = greedy ? p.greedyFactor : 1;
    const fullnessFactor = this.overfull ? CONFIG.fullness.overfullScoreFactor : 1;
    const clarityFactor = this.clarity > 0 ? CONFIG.fullness.clarityScoreFactor : 1;

    const value = base * pace * attachment * this.mindfulness * fullnessFactor * clarityFactor;

    // Aversion: defile. Higher realms fill faster.
    const fill = (CONFIG.fullness.perBite + CONFIG.fullness.perBitePerStage * stage) * (base / 2);
    this.defilement = clamp(this.defilement + fill, 0, CONFIG.fullness.max);

    this.lastBiteAt = this.clock;
    this.lastType = sourceType;

    return { value, greedy, onBeat, newType, full: this.defilement >= CONFIG.fullness.max };
  }

  /** Golden poop purifies as well as feeds. */
  purify(fraction: number) {
    this.defilement = Math.max(0, this.defilement - CONFIG.fullness.max * fraction);
  }

  grantClarity() {
    this.clarity = CONFIG.fullness.clarityDuration;
  }
}
